import React, { useState, useEffect } from 'react';
import {
  Building,
  Search,
  Plus,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Mail,
  Phone,
  MapPin,
  Calendar,
  BookOpen,
  AlertCircle,
  Download,
  User,
  X,
  Image as ImageIcon,
  FileText,
  Globe,
  ExternalLink,
  Shield,
  ChevronLeft,
  ChevronRight,
  Power,
  PowerOff,
  GraduationCap,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import adminService from '../../services/admin.service';
import { API_ENDPOINTS } from '../../config/api';
import { useLocalizedName } from '../../utils/localization';
import { trainingCentersService } from '../../services/training-centers.service';

interface TrainingCenter {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  city?: string;
  country_id: number;
  country: {
    id: number;
    name: string;
  };
  wilaya_id: number;
  wilaya: {
    id: number;
    name: string;
  };
  isActive: boolean;
  isVerified: boolean;
  subscriptionStatus: string;
  totalTrainings: number;
  createdAt: string;
  pendingValidations?: number;
  type: 'consultant' | 'training_center';
  banner_image?: string;
  logo?: string;
  business_registration_pdf?: string;
  tax_id_document?: string;
  other_documents?: string;
  website?: string;
  profile_type?: 'consultant' | 'training_center';
  about?: string;
  category_id?: string;
}

const TrainingCentersPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { getLocalizedName } = useLocalizedName();
  const [centers, setCenters] = useState<TrainingCenter[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVerification, setFilterVerification] = useState('all');
  const [filterCategory, setFilterCategory] = useState('');
  const [categories, setCategories] = useState<{ id: number; name_french?: string; name_english?: string; name?: string }[]>([]);
  const [selectedCenter, setSelectedCenter] = useState<TrainingCenter | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [centerDetails, setCenterDetails] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  useEffect(() => {
    loadTrainingCenters();
    loadCategories();
  }, []);

  useEffect(() => {
    loadCategories();
  }, [i18n.language]);

  const loadCategories = async () => {
    try {
      const categoriesData = await trainingCentersService.getCategories();
      setCategories(categoriesData.map((category: any) => ({
        id: category.id,
        name_french: category.name_french,
        name_english: category.name_english,
        name: category.name || category.name_french
      })));
    } catch (error) {
    }
  };

  const loadTrainingCenters = async () => {
    try {
      setLoading(true);
      // First, get the total count with a minimal request
      const countResponse = await adminService.getTrainingCenters(1, 1);
      const total = countResponse.total;
      
      // Then, fetch all training centers using the total count as limit
      if (total > 0) {
        const response = await adminService.getTrainingCenters(1, total);
        
        const centersWithType = response.centers.map(center => {
          // Use profile_type from backend, with fallback calculation if not provided
          const type = center.profile_type ?? (center.totalTrainings === 0 ? 'consultant' : 'training_center');
          
          return {
            ...center,
            type
          };
        });
        setCenters(centersWithType);
      } else {
        setCenters([]);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const filteredCenters = centers
    .filter(center => {
    const matchesSearch = center.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         center.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (center.city && center.city.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (center.wilaya?.name && center.wilaya.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (center.country?.name && center.country.name.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && center.isActive) ||
                         (filterStatus === 'inactive' && !center.isActive);
    const matchesVerification = filterVerification === 'all' ||
                               (filterVerification === 'verified' && center.isVerified) ||
                               (filterVerification === 'unverified' && !center.isVerified);
    const matchesCategory = filterCategory === '' || 
                           center.category_id === filterCategory;
      const isTrainingCenter = (center.type ?? center.profile_type) === 'training_center';
      return matchesSearch && matchesStatus && matchesVerification && matchesCategory && isTrainingCenter;
    });

  // Pagination calculations
  const totalPages = Math.ceil(filteredCenters.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCenters = filteredCenters.slice(startIndex, endIndex);

  // Reset to page 1 when search term or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterVerification, filterCategory]);

  const getSubscriptionStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'expired': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSubscriptionStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Actif';
      case 'expired': return 'Expiré';
      case 'pending': return 'En attente';
      default: return status;
    }
  };

  const handleViewDetails = async (center: TrainingCenter) => {
    setSelectedCenter(center);
    setShowDetailsModal(true);
    setLoadingDetails(true);
    
    try {
      // Fetch detailed information about the training center/consultant
      const response = await adminService.getTrainingCenterById(center.id);
      setCenterDetails(response);
    } catch (error) {
      // Fallback to basic center info if detailed fetch fails
      setCenterDetails(center);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleToggleCenterStatus = async (center: TrainingCenter) => {
    const newStatus = !center.isActive;
    const action = newStatus ? 'activer' : 'désactiver';
    
    if (!window.confirm(`Voulez-vous ${action} le compte de ${center.name} ?`)) {
      return;
    }

    try {
      // Keep existing verification status when toggling active status
      await adminService.updateTrainingCenterStatus(center.id, newStatus, center.isVerified);
      setCenters(prev => prev.map(c => 
        c.id === center.id ? { ...c, isActive: newStatus } : c
      ));
      // Also update if it's in the details modal
      if (selectedCenter?.id === center.id) {
        setSelectedCenter({ ...selectedCenter, isActive: newStatus });
      }
      if (centerDetails?.id === center.id) {
        setCenterDetails({ ...centerDetails, isActive: newStatus });
      }
    } catch (error) {
      alert(`Erreur lors de la ${action} du compte`);
    }
  };

  const handleDeleteCenter = async (center: TrainingCenter) => {
   

    if (!center.id) {
      try {
        const details = await adminService.getTrainingCenterById(center.id);
       
      } catch (e) {
      }
    }

    if (!center.id) {
      alert("Impossible de déterminer l'utilisateur associé à ce centre.");
      return;
    }

    if (window.confirm('Supprimer ce centre et toutes ses données ? Cette action est irréversible.')) {
      try {
        await adminService.deleteTrainingCenter(center.id);
        setCenters(prev => prev.filter(c => c.id !== center.id));
      } catch (e) {
        alert('Erreur lors de la suppression');
      }
    }
  };



  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedCenter(null);
    setCenterDetails(null);
  };

  const stats = {
    total: centers.length,
    verified: centers.filter(c => c.isVerified).length,
    pending: centers.filter(c => !c.isVerified).length,
    totalTrainings: centers.reduce((sum, center) => sum + center.totalTrainings, 0),
    pendingValidations: centers.reduce((sum, center) => sum + (center.pendingValidations || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Centres de formation</h1>
          <p className="text-gray-600 mt-1">Gérez tous les centres de formation de la plateforme</p>
        </div>

      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-brand-primary/10 rounded-lg">
              <Building className="h-6 w-6 text-brand-primary" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total centres</p>
              <h3 className="text-2xl font-bold text-gray-900">{stats.total}</h3>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Centres vérifiés</p>
              <h3 className="text-2xl font-bold text-gray-900">{stats.verified}</h3>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">En attente</p>
              <h3 className="text-2xl font-bold text-gray-900">{stats.pending}</h3>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <BookOpen className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Formations totales</p>
              <h3 className="text-2xl font-bold text-gray-900">{stats.totalTrainings}</h3>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-orange-100 rounded-lg">
              <AlertCircle className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Validations en attente</p>
              <h3 className="text-2xl font-bold text-gray-900">{stats.pendingValidations}</h3>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Rechercher des centres..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          >
            <option value="">{t('admin.trainingCenters.allCategories', 'Toutes les catégories')}</option>
            {categories.map(category => (
              <option key={category.id} value={String(category.id)}>
                {getLocalizedName(category)}
              </option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          >
            <option value="all">Tous les statuts</option>
            <option value="active">Actif</option>
            <option value="inactive">Inactif</option>
          </select>
          <select
            value={filterVerification}
            onChange={(e) => setFilterVerification(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          >
            <option value="all">Toutes les vérifications</option>
            <option value="verified">Vérifiés</option>
            <option value="unverified">Non vérifiés</option>
          </select>
        </div>
      </div>

      {/* Centers Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Centre de formation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
               
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vérification
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Abonnement
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Formations
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedCenters.map((center) => (
                <tr key={center.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 bg-brand-primary/10 rounded-full flex items-center justify-center">
                        <Building className="h-5 w-5 text-brand-primary" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{center.name}</div>
                        <div className="text-sm text-gray-500">{center.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <Phone className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-900">{center.phone}</span>
                    </div>
                  </td>
             
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {center.isActive ? (
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500 mr-2" />
                      )}
                      <span className={`text-sm ${center.isActive ? 'text-green-600' : 'text-red-600'}`}>
                        {center.isActive ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {center.isVerified ? (
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      ) : (
                        <Clock className="h-4 w-4 text-yellow-500 mr-2" />
                      )}
                      <span className={`text-sm ${center.isVerified ? 'text-green-600' : 'text-yellow-600'}`}>
                        {center.isVerified ? 'Vérifié' : 'En attente'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSubscriptionStatusColor(center.subscriptionStatus)}`}>
                      {getSubscriptionStatusLabel(center.subscriptionStatus)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <BookOpen className="h-4 w-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">{center.totalTrainings}</span>
                      {center.pendingValidations && center.pendingValidations > 0 && (
                        <span className="bg-orange-100 text-orange-800 text-xs font-medium px-2 py-1 rounded-full">
                          {center.pendingValidations} en attente
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button 
                        onClick={() => handleViewDetails(center)}
                        className="text-brand-primary hover:opacity-90"
                        title="Voir"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => handleToggleCenterStatus(center)}
                        className={center.isActive ? "text-orange-600 hover:text-orange-900" : "text-green-600 hover:text-green-900"}
                        title={center.isActive ? "Désactiver" : "Activer"}
                      >
                        {center.isActive ? <PowerOff size={16} /> : <Power size={16} />}
                      </button>
                      <button 
                        onClick={() => handleDeleteCenter(center)}
                        className="text-red-600 hover:text-red-900"
                        title="Supprimer"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredCenters.length === 0 && (
          <div className="text-center py-12">
            <Building className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Aucun centre de formation trouvé</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {filteredCenters.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Affichage de <span className="font-semibold">{startIndex + 1}</span> à{' '}
              <span className="font-semibold">{Math.min(endIndex, filteredCenters.length)}</span> sur{' '}
              <span className="font-semibold">{filteredCenters.length}</span> centres
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors flex items-center gap-1"
              >
                <ChevronLeft size={16} />
                <span>Précédent</span>
              </button>
              <div className="flex items-center space-x-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-2 min-w-[40px] rounded-lg transition-colors ${
                          currentPage === page
                            ? 'bg-brand-primary text-white'
                            : 'border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return <span key={page} className="px-2">...</span>;
                  }
                  return null;
                })}
              </div>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors flex items-center gap-1"
              >
                <span>Suivant</span>
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedCenter && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-brand-primary/10 rounded-xl">
                  {selectedCenter.type === 'consultant' ? (
                    <User className="h-6 w-6 text-brand-primary" />
                  ) : (
                    <Building className="h-6 w-6 text-brand-primary" />
                  )}
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{selectedCenter.name}</h2>
                  <p className="text-gray-600">
                    {selectedCenter.type === 'consultant' ? 'Consultant' : 'Centre de formation'}
                  </p>
                </div>
              </div>
              <button
                onClick={closeDetailsModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-6 w-6 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {loadingDetails ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
                  <span className="ml-3 text-gray-600">Chargement des détails...</span>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Header with Logo and Basic Info */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Logo on Left */}
                    <div className="flex items-center justify-center">
                      {centerDetails?.logo ? (
                        <img 
                          src={centerDetails.logo.startsWith('http') ? centerDetails.logo : `${API_ENDPOINTS.UPLOADS}/${centerDetails.logo}`}
                          alt={`Logo ${centerDetails.name}`}
                          className="w-32 h-32 object-contain rounded-lg border-2 border-gray-200"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-32 h-32 rounded-lg border-2 border-gray-200 flex items-center justify-center bg-gray-50">
                          <Building className="h-16 w-16 text-gray-400" />
                        </div>
                      )}
                    </div>

                    {/* Basic Info and Status on Right */}
                    <div className="md:col-span-2 space-y-4">
                      {/* Status Cards */}
                      <div className="grid grid-cols-3 gap-4">
                        <div className="bg-gray-50 rounded-xl p-4">
                          <div className="flex items-center space-x-3">
                            {(centerDetails?.isActive ?? selectedCenter?.isActive) ? (
                              <CheckCircle className="h-5 w-5 text-green-500" />
                            ) : (
                              <XCircle className="h-5 w-5 text-red-500" />
                            )}
                            <div>
                              <p className="text-sm text-gray-600">Statut</p>
                              <p className={`font-semibold ${(centerDetails?.isActive ?? selectedCenter?.isActive) ? 'text-green-600' : 'text-red-600'}`}>
                                {(centerDetails?.isActive ?? selectedCenter?.isActive) ? 'Actif' : 'Inactif'}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-4">
                          <div className="flex items-center space-x-3">
                            {(centerDetails?.isVerified ?? selectedCenter?.isVerified) ? (
                              <CheckCircle className="h-5 w-5 text-green-500" />
                            ) : (
                              <Clock className="h-5 w-5 text-yellow-500" />
                            )}
                            <div>
                              <p className="text-sm text-gray-600">Vérification</p>
                              <p className={`font-semibold ${(centerDetails?.isVerified ?? selectedCenter?.isVerified) ? 'text-green-600' : 'text-yellow-600'}`}>
                                {(centerDetails?.isVerified ?? selectedCenter?.isVerified) ? 'Vérifié' : 'En attente'}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-4">
                          <div className="flex items-center space-x-3">
                            <BookOpen className="h-5 w-5 text-blue-500" />
                            <div>
                              <p className="text-sm text-gray-600">Formations</p>
                              <p className="font-semibold text-blue-600">{centerDetails?.totalTrainings ?? selectedCenter?.totalTrainings ?? 0}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Website if available */}
                      {centerDetails?.website && (
                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                          <div className="flex items-center gap-2">
                            <Globe className="h-4 w-4 text-gray-600" />
                            <span className="text-sm text-gray-600">Site web:</span>
                            <a 
                              href={centerDetails.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-sm font-semibold text-brand-primary hover:underline flex items-center gap-1"
                            >
                              {centerDetails.website} <ExternalLink size={12} />
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description/About */}
                  {centerDetails?.about && (
                    <div className="bg-gray-50 rounded-xl p-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                        <AlertCircle className="h-4 w-4 text-gray-600" />
                        <span>À propos</span>
                      </h3>
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {centerDetails.about}
                      </p>
                    </div>
                  )}

                  {/* Contact Information */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                      <Mail className="h-4 w-4 text-gray-600" />
                      <span>Contact</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center space-x-3">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-600">Email</p>
                          <p className="font-medium text-gray-900">{centerDetails?.email ?? selectedCenter?.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-600">Téléphone</p>
                          <p className="font-medium text-gray-900">{centerDetails?.phone ?? selectedCenter?.phone}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Location Information */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                      <MapPin className="h-4 w-4 text-gray-600" />
                      <span>Localisation</span>
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <MapPin className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-600">Adresse</p>
                          <p className="font-medium text-gray-900">{centerDetails?.address ?? selectedCenter?.address}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600">Ville</p>
                          <p className="font-medium text-gray-900">{centerDetails?.city ?? selectedCenter?.city ?? 'Non spécifiée'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Wilaya</p>
                          <p className="font-medium text-gray-900">{centerDetails?.wilaya?.name ?? selectedCenter?.wilaya?.name ?? 'Non spécifiée'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Subscription Information */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-gray-600" />
                      <span>Abonnement</span>
                    </h3>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Statut de l'abonnement</p>
                        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getSubscriptionStatusColor(centerDetails?.subscriptionStatus ?? selectedCenter?.subscriptionStatus ?? '')}`}>
                          {getSubscriptionStatusLabel(centerDetails?.subscriptionStatus ?? selectedCenter?.subscriptionStatus ?? '')}
                        </span>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Membre depuis</p>
                        <p className="font-medium text-gray-900">
                          {new Date(centerDetails?.createdAt ?? selectedCenter?.createdAt ?? '').toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Additional Information */}
                  {(centerDetails?.pendingValidations ?? selectedCenter?.pendingValidations) && (centerDetails?.pendingValidations ?? selectedCenter?.pendingValidations) > 0 && (
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
                      <div className="flex items-center space-x-3">
                        <AlertCircle className="h-5 w-5 text-orange-500" />
                        <div>
                          <p className="text-sm font-medium text-orange-800">
                            {(centerDetails?.pendingValidations ?? selectedCenter?.pendingValidations)} validation(s) en attente
                          </p>
                          <p className="text-sm text-orange-600">
                            Ce centre a des formations qui nécessitent une validation
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Banner Image */}
                  {centerDetails?.banner_image && (
                    <div className="bg-gray-50 rounded-xl p-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                        <ImageIcon className="h-4 w-4 text-gray-600" />
                        <span>Bannière</span>
                      </h3>
                      <div className="bg-white rounded-lg overflow-hidden shadow-sm">
                        <img 
                          src={centerDetails.banner_image.startsWith('http') ? centerDetails.banner_image : `${API_ENDPOINTS.UPLOADS}/${centerDetails.banner_image}`}
                          alt={`Bannière ${centerDetails.name}`}
                          className="w-full h-64 object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Documentation & PDFs */}
                  {(centerDetails?.business_registration_pdf || centerDetails?.tax_id_document || centerDetails?.other_documents) && (
                    <div className="bg-gray-50 rounded-xl p-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                        <FileText className="h-4 w-4 text-gray-600" />
                        <span>Documents</span>
                      </h3>
                      <div className="space-y-3">
                        {centerDetails.business_registration_pdf && (
                          <div className="bg-white p-4 rounded-lg border border-gray-200 flex items-center justify-between hover:shadow-md transition-shadow">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-red-100 rounded-lg">
                                <FileText className="h-5 w-5 text-red-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">Régistre de commerce</p>
                                <p className="text-sm text-gray-500">Document officiel</p>
                              </div>
                            </div>
                            <a
                              href={centerDetails.business_registration_pdf.startsWith('http') ? centerDetails.business_registration_pdf : `${API_ENDPOINTS.UPLOADS}/${centerDetails.business_registration_pdf}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:opacity-90 transition-opacity flex items-center space-x-2"
                            >
                              <Download size={16} />
                              <span>Télécharger</span>
                            </a>
                          </div>
                        )}
                        {centerDetails.tax_id_document && (
                          <div className="bg-white p-4 rounded-lg border border-gray-200 flex items-center justify-between hover:shadow-md transition-shadow">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-blue-100 rounded-lg">
                                <FileText className="h-5 w-5 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">Numéro d'identification fiscale</p>
                                <p className="text-sm text-gray-500">Document officiel</p>
                              </div>
                            </div>
                            <a
                              href={centerDetails.tax_id_document.startsWith('http') ? centerDetails.tax_id_document : `${API_ENDPOINTS.UPLOADS}/${centerDetails.tax_id_document}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:opacity-90 transition-opacity flex items-center space-x-2"
                            >
                              <Download size={16} />
                              <span>Télécharger</span>
                            </a>
                          </div>
                        )}
                        {centerDetails.other_documents && (
                          <div className="bg-white p-4 rounded-lg border border-gray-200 flex items-center justify-between hover:shadow-md transition-shadow">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-purple-100 rounded-lg">
                                <FileText className="h-5 w-5 text-purple-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">Autres documents</p>
                                <p className="text-sm text-gray-500">Fichiers supplémentaires</p>
                              </div>
                            </div>
                            <a
                              href={centerDetails.other_documents.startsWith('http') ? centerDetails.other_documents : `${API_ENDPOINTS.UPLOADS}/${centerDetails.other_documents}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:opacity-90 transition-opacity flex items-center space-x-2"
                            >
                              <Download size={16} />
                              <span>Télécharger</span>
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Additional Information */}
                  {centerDetails && (
                    <div className="bg-gradient-to-br from-brand-primary/5 to-purple-50 rounded-xl p-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {/* Type */}
                        {centerDetails.type && (
                          <div>
                            <p className="text-xs text-gray-600">Type</p>
                            <p className="text-sm font-semibold text-gray-900 capitalize">{centerDetails.type}</p>
                          </div>
                        )}

                        {/* Country */}
                        {centerDetails.country && (
                          <div>
                            <p className="text-xs text-gray-600">Pays</p>
                            <p className="text-sm font-semibold text-gray-900">{centerDetails.country.name}</p>
                          </div>
                        )}

                        {/* Wilaya */}
                        {centerDetails.wilaya && (
                          <div>
                            <p className="text-xs text-gray-600">Wilaya</p>
                            <p className="text-sm font-semibold text-gray-900">{centerDetails.wilaya.name}</p>
                          </div>
                        )}

                        {/* Profile Type */}
                        {centerDetails.profile_type && (
                          <div>
                            <p className="text-xs text-gray-600">Profil</p>
                            <p className="text-sm font-semibold text-gray-900 capitalize">{centerDetails.profile_type}</p>
                          </div>
                        )}

                        {/* Website */}
                        {centerDetails.website && (
                          <div>
                            <p className="text-xs text-gray-600">Site web</p>
                            <a 
                              href={centerDetails.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-sm font-semibold text-brand-primary hover:underline flex items-center gap-1"
                            >
                              Visiter <ExternalLink size={12} />
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={closeDetailsModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Fermer
              </button>
              {/* <button className="px-4 py-2 text-white bg-brand-primary hover:opacity-90 rounded-lg transition-colors">
                Modifier
              </button> */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingCentersPage;
